/**
 * ModulePage — fetches a module manifest by ID, then fetches each data source,
 * and renders the manifest layout via the declarative module renderer.
 *
 * Data sources are fetched in parallel. The page renders as soon as the manifest
 * is available; data-dependent sections show loading placeholders while fetching.
 *
 * The api client handles silent token refresh transparently. An UnauthorizedError
 * reaching this component means the console session is truly gone.
 */
import { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { api, UnauthorizedError } from '../api/client'
import type { Manifest } from '../api/client'
import { renderNode } from '../modules/renderNode'

export function ModulePage() {
  const { id } = useParams<{ id: string }>()

  const [manifest, setManifest] = useState<Manifest | null>(null)
  const [dataBag, setDataBag] = useState<Record<string, unknown>>({})
  const [manifestError, setManifestError] = useState<string | null>(null)
  const [dataErrors, setDataErrors] = useState<Record<string, string>>({})
  const [loadingData, setLoadingData] = useState(false)

  // Fetch manifest once on mount (or when id changes).
  useEffect(() => {
    if (!id) return
    let cancelled = false
    setManifest(null)
    setManifestError(null)
    setDataBag({})
    setDataErrors({})

    api.getModule(id).then(m => {
      if (!cancelled) setManifest(m)
    }).catch(err => {
      if (!cancelled) {
        if (err instanceof UnauthorizedError) {
          setManifestError('Session expired.')
        } else {
          setManifestError(err instanceof Error ? err.message : 'Failed to load module.')
        }
      }
    })
    return () => { cancelled = true }
  }, [id])

  // Fetch all data sources once manifest is loaded.
  const fetchData = useCallback(async (m: Manifest) => {
    if (!id || m.data_sources.length === 0) return
    setLoadingData(true)

    const results = await Promise.allSettled(
      m.data_sources.map(async src => {
        const res = await api.getModuleData(id, src.id)
        return { id: src.id, data: res.data }
      })
    )

    const bag: Record<string, unknown> = {}
    const errs: Record<string, string> = {}
    for (const result of results) {
      if (result.status === 'fulfilled') {
        bag[result.value.id] = result.value.data
      } else {
        // Find the source id via index — allSettled preserves order.
        const idx = results.indexOf(result)
        const srcId = m.data_sources[idx]?.id ?? `source_${idx}`
        errs[srcId] = result.reason instanceof Error ? result.reason.message : 'Data fetch failed.'
      }
    }

    setDataBag(bag)
    setDataErrors(errs)
    setLoadingData(false)
  }, [id])

  useEffect(() => {
    if (manifest) {
      void fetchData(manifest)
    }
  }, [manifest, fetchData])

  if (!id) {
    return <p className="text-sm text-red-600">No module ID in URL.</p>
  }

  if (manifestError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {manifestError}
      </div>
    )
  }

  if (!manifest) {
    return <div className="text-sm text-gray-400 animate-pulse">Loading module...</div>
  }

  return (
    <div className="space-y-4">
      {/* Module header */}
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {manifest.icon ? `${manifest.icon} ` : ''}{manifest.title}
          </h1>
          <p className="text-xs text-gray-400 mt-1">
            v{manifest.version} · {manifest.module_id}
          </p>
        </div>
        {loadingData && (
          <p className="text-xs text-gray-400 animate-pulse">Loading data...</p>
        )}
      </div>

      {/* Data source errors (non-fatal) */}
      {Object.keys(dataErrors).length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 space-y-1">
          <p className="font-medium">Some data sources failed to load:</p>
          <ul className="list-disc list-inside text-xs">
            {Object.entries(dataErrors).map(([srcId, msg]) => (
              <li key={srcId}><strong>{srcId}</strong>: {msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Rendered module layout */}
      <div>
        {renderNode(manifest.layout, dataBag)}
      </div>
    </div>
  )
}
